// App.js - Updated with Smart App Banner
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import Welcome from './pages/Welcome';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ProfileSetup from './pages/ProfileSetup';
import Profile from './pages/Profile';
import OtherUserProfile from './pages/OtherUserProfile';
import EditProfile from './pages/EditProfile';
import Settings from './pages/Settings';
import AccountDetails from './pages/AccountDetails';
import Home from './pages/Home';
// Explore, Invitations and Communities are no longer routed — /explore,
// /invitations and /communities redirect to Home. The pages stay in the
// tree; not importing them here keeps them out of the bundle.
import EventInvitation from './pages/EventInvitation';
import InvitationPreviewFrame from './pages/InvitationPreviewFrame';
import AuthCallback from './pages/AuthCallback';
import { startEventTracking } from './services/track';
import GuestLists from './pages/GuestLists';
import GuestListDetail from './pages/GuestListDetail';
import CommunityProfile from './pages/CommunityProfile';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';
import Refunds from './pages/Refunds';
import { AuthProvider, useAuth } from './services/auth';
import UpgradeEventSelection from './pages/UpgradeEventSelection';
import UpgradeTiers from './pages/UpgradeTiers';
import CreateEvent from './pages/CreateEvent';
import RemoveAccount from './pages/RemoveAccount';
import SmartAppBanner from './components/SmartAppBanner';
import CoverImageTemplateUploader from './pages/CoverImageTemplateUploader';
import CoverImageTemplateBrowser from './pages/CoverImageTemplateBrowser';
import GreetingCards from './pages/GreetingCards';
import CreateGreetingCard from './pages/CreateGreetingCard';
import ViewGreetingCard from './pages/ViewGreetingCard';
import Invoices from './pages/Invoices';
import Download from './pages/Download';
import { startCoverPickAhead } from './services/coverPickAhead';
import StandaloneAlbum from './pages/StandaloneAlbum';
import Notifications from './pages/Notifications';

// Product events for Invits Office's funnels (services/track.js): batched,
// never on a page's critical path, and silent inside the preview iframe.
startEventTracking();

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) return children;

  // Where they were going, so signing in finishes the trip.
  //
  // This was a bare <Navigate to="/login" />, which threw the destination
  // away: someone clicking "Make a card" landed on the login form and then,
  // having signed in, on their profile — with no card, and no sign that the
  // product had forgotten what they asked for. Login already knows how to
  // honour `redirectTo`; it was simply never given one.
  return (
    <Navigate
      to="/login"
      replace
      state={{ redirectTo: `${location.pathname}${location.search}` }}
    />
  );
};

/** /create-event -> /create-invitation, keeping the query and any state. */
const CreateInvitationRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/create-invitation${location.search}`} state={location.state} replace />;
};

const RedirectToProfile = () => {
  const { user, loading } = useAuth();
  const [profile, setProfile] = React.useState(null);
  const [profileLoading, setProfileLoading] = React.useState(true);

  // The profile's username, for the redirect. Signed out, there is nothing to
  // load - which used to leave profileLoading true forever, so a signed-out
  // visitor saw a spinner instead of being sent to /login.
  React.useEffect(() => {
    if (loading) return undefined;
    if (!user) {
      setProfileLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const supabase = (await import('./services/supabase')).default;
        const { data, error } = await supabase
          .from('users')
          .select('username')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        if (!cancelled) setProfile(data);
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading]);

  if (loading || profileLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (profile?.username) {
    return <Navigate to={`/${profile.username}`} replace />;
  }

  return <Navigate to="/profile-setup" replace />;
};

// Component to determine if showing own profile, other user's profile, or community profile
const ProfileRouter = () => {
  const { user } = useAuth();
  const { username } = useParams();
  const [isOwnProfile, setIsOwnProfile] = React.useState(false);
  const [isCommunity, setIsCommunity] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  // Community, own profile or someone else's, for this username. Everything
  // is reset first: moving from a community's page to a person's kept
  // isCommunity from the last one and showed the wrong page.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setIsCommunity(false);
    setIsOwnProfile(false);

    (async () => {
    try {
      const supabase = (await import('./services/supabase')).default;

      // First check if it's a community
      // maybeSingle, not single: "no community by that name" is the normal
      // answer here, and .single() turns it into a 406 in the console on
      // every profile view.
      const { data: communityData, error: communityError } = await supabase
        .from('communities')
        .select('username')
        .eq('username', username)
        .maybeSingle();

      if (cancelled) return;
      if (communityData && !communityError) {
        setIsCommunity(true);
        return;
      }

      // If not a community, check if it's a user profile
      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('username')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        if (!cancelled) setIsOwnProfile(data.username === username);
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      if (!cancelled) setLoading(false);
    }
    })();
    return () => { cancelled = true; };
  }, [username, user]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (isCommunity) {
    return <CommunityProfile />;
  }

  return isOwnProfile ? <Profile /> : <OtherUserProfile />;
};

function App() {
  // The next new invitation's look - a random cover template, sometimes with
  // a video theme - fetched while the browser is idle, so Create opens on it
  // instantly. See services/coverPickAhead.js.
  React.useEffect(() => startCoverPickAhead(), []);

  return (
    <Router>
      <AuthProvider>
        {/* ✅ Smart App Banner - Shows globally on mobile */}
        <SmartAppBanner />
        
        <Routes>
          {/* Home & Auth Routes */}
          <Route path="/" element={<Welcome />} />
          <Route path="/login" element={<Login />} />
          {/* Google / Apple sign-in lands here (services/socialAuth.js). */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/CoverImageTemplateUploader" element={<CoverImageTemplateUploader/>} />
          <Route path="/CoverImageTemplateBrowser" element={<CoverImageTemplateBrowser/>} />
          <Route path="/download" element={<Download />} />
          
          {/* Protected Routes */}
          <Route 
            path="/profile-setup" 
            element={
              <ProtectedRoute>
                <ProfileSetup />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/edit-profile" 
            element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            } 
          />
          
          {/* Create is NOT behind ProtectedRoute.
              Letting someone build an invitation and meet the sign-in only at
              publish is the whole point of the change — the old route bounced
              every first-time visitor to /login before they had seen what the
              product does. CreateEvent keeps the draft and finishes the job
              after they sign in. Editing still needs an account, but the guard
              for that belongs to the row, not the route: the page loads the
              invitation and the database refuses a stranger's update. */}
          <Route path="/create-invitation" element={<CreateEvent />} />
          {/* The old address. Links already out there - bookmarks, emails, the
              "resume after sign-in" redirect of anyone mid-flow - keep working,
              with their ?type= and ?resume= intact. */}
          <Route path="/create-event" element={<CreateInvitationRedirect />} />

          <Route 
            path="/edit-event/:eventId" 
            element={
              <ProtectedRoute>
                <CreateEvent />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/account-details" 
            element={
              <ProtectedRoute>
                <AccountDetails />
              </ProtectedRoute>
            } 
          />
          
          {/* Home: your own invitations and cards. The landing surface, and
              the Invites tab. */}
          <Route path="/home" element={<Home />} />

          {/* De-emphasised, not deleted — the same treatment Explore and
              Communities get in the app's route table, where they are
              `hidden: true`: still reachable by name, no longer advertised.
              Every link ever sent for these still resolves, it just arrives
              somewhere useful. Explore's and Invitations' components stay in
              the tree so nothing that imports them breaks. */}
          <Route path="/explore" element={<Navigate to="/home" replace />} />
          <Route path="/invitations" element={<Navigate to="/home" replace />} />

          {/* Greeting Cards Routes */}
          <Route
            path="/greeting-cards"
            element={
              <ProtectedRoute>
                <GreetingCards />
              </ProtectedRoute>
            }
          />

          {/* Also not behind ProtectedRoute, for the same reason as
              /create-event: you can make the thing, and the account is asked
              for when you save it. CreateGreetingCard keeps the draft. */}
          <Route path="/create-greeting-card" element={<CreateGreetingCard />} />

          <Route
            path="/edit-greeting-card/:cardId"
            element={
              <ProtectedRoute>
                <CreateGreetingCard />
              </ProtectedRoute>
            }
          />

          {/* Guest Lists Routes */}
          <Route 
            path="/guest-lists" 
            element={
              <ProtectedRoute>
                <GuestLists />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/guest-list/:id" 
            element={
              <ProtectedRoute>
                <GuestListDetail />
              </ProtectedRoute>
            } 
          />

          <Route path="/communities" element={<Navigate to="/home" replace />} />

          <Route 
            path="/upgrade" 
            element={
              <ProtectedRoute>
                <UpgradeEventSelection />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/upgrade/tiers"
            element={
              <ProtectedRoute>
                <UpgradeTiers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />

          {/* Dashboard - Redirect to user's profile */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <RedirectToProfile />
              </ProtectedRoute>
            } 
          />

          {/* Profile by username - checks if own profile or other user */}
          <Route 
            path="/:username" 
            element={
              <ProtectedRoute>
                <ProfileRouter />
              </ProtectedRoute>
            } 
          />
          
          {/* Event Invitation Routes - Public access with privacy checks inside */}
          {/* /i/:slug is what every invitation is shared as now; /invite/:slug
              stays because every link sent before that still uses it.
              React Router ranks both above the /:username catch-all. */}
          {/* The create form's live preview, loaded in an iframe. */}
          <Route path="/invitation-preview" element={<InvitationPreviewFrame />} />
          <Route path="/i/:slug" element={<EventInvitation />} />
          <Route path="/invite/:slug" element={<EventInvitation />} />
          <Route path="/event/:slug" element={<EventInvitation />} />
          <Route path="/album/:slug" element={<StandaloneAlbum />} />

          {/* Greeting Card Route - Public access with privacy checks inside */}
          <Route path="/c/:slug" element={<ViewGreetingCard />} />
          <Route path="/card/:slug" element={<ViewGreetingCard />} />
          <Route path="/cards/:slug" element={<ViewGreetingCard />} />

          {/* Legal Pages */}
          <Route path="/terms-and-conditions" element={<Terms />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/remove-account" element={<RemoveAccount />} />
          
          {/* 404 - Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;